import {
  takeLatest,
  takeEvery,
  call,
  put,
  fork,
  cancel,
  delay,
} from 'redux-saga/effects'
import {
  FETCH_VEHICLE_SNAPSHOTS,
  CREATE_VEHICLE_SNAPSHOT,
  DELETE_VEHICLE_SNAPSHOT,
  FETCH_VEHICLE_AS_BUILT,
  vehicleSnapshotsReceived,
  vehicleSnapshotsFailed,
  vehicleSnapshotCreated,
  vehicleSnapshotCreateFailed,
  vehicleSnapshotDeleted,
  vehicleSnapshotDeleteFailed,
  vehicleAsBuiltReceived,
  vehicleAsBuiltFailed,
} from 'actions/vehicleSnapshots'
import {
  fetchVehicleSnapshots as fetchService,
  createVehicleSnapshot as createService,
  deleteVehicleSnapshot as deleteService,
  fetchVehicleAsBuilt as fetchAsBuiltService,
} from 'services/vehicleSnapshots'
import {
  createSystemNotification,
  deleteSystemNotification,
} from 'actions/application'
import {
  NOTIFICATION_SUCCESS,
  NOTIFICATION_ERROR,
  NOTIFICATION_INFO,
  DTNA_CASE_MANAGEMENT_URL,
} from 'utils/constants'
import strings from 'utils/strings'

const SNAPSHOT_CREATING_NOTIFICATION_ID = 'snapshot-creating-in-progress'

function* showCreatingNotificationAfterDelay() {
  yield delay(2000)
  yield put(
    createSystemNotification({
      notificationId: SNAPSHOT_CREATING_NOTIFICATION_ID,
      notificationMessage: strings.snapshot_creating,
      notificationType: NOTIFICATION_INFO,
      persistent: true,
      async: true,
    })
  )
}

export function* watchFetchSnapshots() {
  yield takeEvery(FETCH_VEHICLE_SNAPSHOTS, fetchSnapshotsSaga)
}

export function* watchCreateSnapshot() {
  yield takeLatest(CREATE_VEHICLE_SNAPSHOT, createSnapshotSaga)
}

export function* watchDeleteSnapshot() {
  yield takeEvery(DELETE_VEHICLE_SNAPSHOT, deleteSnapshotSaga)
}

export function* fetchSnapshotsSaga({ payload: { serialNumber } }) {
  try {
    const response = yield call(fetchService, { serialNumber })
    const data = response.data !== undefined ? response.data : response
    yield put(
      vehicleSnapshotsReceived(
        serialNumber,
        Array.isArray(data) ? data : data.snapshots || []
      )
    )
  } catch (error) {
    yield put(vehicleSnapshotsFailed(serialNumber, error.message))
  }
}

export function* createSnapshotSaga({ payload: { serialNumber } }) {
  const inProgressTask = yield fork(showCreatingNotificationAfterDelay)
  try {
    const response = yield call(createService, { serialNumber })
    yield cancel(inProgressTask)
    yield put(
      deleteSystemNotification({
        notificationId: SNAPSHOT_CREATING_NOTIFICATION_ID,
      })
    )
    const created = response.data !== undefined ? response.data : response
    yield put(vehicleSnapshotCreated(serialNumber, created.snapshot ?? created))
    // Re-fetch to get server-assigned id, name, expiresAt
    const listResponse = yield call(fetchService, { serialNumber })
    const listData =
      listResponse.data !== undefined ? listResponse.data : listResponse
    yield put(
      vehicleSnapshotsReceived(
        serialNumber,
        Array.isArray(listData) ? listData : listData.snapshots || []
      )
    )
    yield put(
      createSystemNotification({
        notificationMessage: strings.snapshot_created,
        notificationType: NOTIFICATION_SUCCESS,
        persistent: true,
        closable: true,
      })
    )
  } catch (error) {
    yield cancel(inProgressTask)
    yield put(
      deleteSystemNotification({
        notificationId: SNAPSHOT_CREATING_NOTIFICATION_ID,
      })
    )
    if (error.status === 409) {
      // 409 = server-enforced limit — clear saving state, re-fetch to correct
      // stale snapshot count so the Capture slot is correctly disabled
      yield put(vehicleSnapshotCreateFailed(serialNumber, null))
      try {
        const listResponse = yield call(fetchService, { serialNumber })
        const listData =
          listResponse.data !== undefined ? listResponse.data : listResponse
        yield put(
          vehicleSnapshotsReceived(
            serialNumber,
            Array.isArray(listData) ? listData : listData.snapshots || []
          )
        )
      } catch (_fetchError) {
        // ignore — notification still fires below
      }
      yield put(
        createSystemNotification({
          notificationMessage: strings.snapshot_create_limit,
          notificationType: NOTIFICATION_ERROR,
          persistent: true,
          closable: true,
        })
      )
    } else {
      yield put(vehicleSnapshotCreateFailed(serialNumber, null))
      yield put(
        createSystemNotification({
          notificationMessage: strings.snapshot_save_error,
          notificationType: NOTIFICATION_ERROR,
          persistent: true,
          closable: true,
          reportLink: {
            text: strings.salesforce,
            href: DTNA_CASE_MANAGEMENT_URL,
            trailingPeriod: true,
          },
        })
      )
    }
  }
}

export function* watchFetchAsBuilt() {
  yield takeLatest(FETCH_VEHICLE_AS_BUILT, fetchAsBuiltSaga)
}

export function* fetchAsBuiltSaga({ payload: { serialNumber } }) {
  try {
    const response = yield call(fetchAsBuiltService, { serialNumber })
    const data = response.data !== undefined ? response.data : response
    yield put(vehicleAsBuiltReceived(serialNumber, data))
  } catch (error) {
    yield put(vehicleAsBuiltFailed(serialNumber, error.message))
  }
}

export function* deleteSnapshotSaga({ payload: { serialNumber, snapshotId } }) {
  try {
    yield call(deleteService, { serialNumber, snapshotId })
    yield put(vehicleSnapshotDeleted(serialNumber, snapshotId))
    yield put(
      createSystemNotification({
        notificationMessage: strings.snapshot_deleted,
        notificationType: NOTIFICATION_SUCCESS,
        persistent: true,
        closable: true,
      })
    )
  } catch (error) {
    if (error.status === 404) {
      // 404 = already deleted elsewhere — silently remove from local state
      yield put(vehicleSnapshotDeleted(serialNumber, snapshotId))
    } else if (error.status === 403) {
      // 403 = not the owner — show a friendly inline message, no global notification
      yield put(
        vehicleSnapshotDeleteFailed(
          serialNumber,
          snapshotId,
          strings.snapshot_delete_own_only
        )
      )
    } else {
      yield put(vehicleSnapshotDeleteFailed(serialNumber, snapshotId, null))
      yield put(
        createSystemNotification({
          notificationMessage: strings.snapshot_save_error,
          notificationType: NOTIFICATION_ERROR,
          persistent: true,
          closable: true,
          reportLink: {
            text: strings.salesforce,
            href: DTNA_CASE_MANAGEMENT_URL,
            trailingPeriod: true,
          },
        })
      )
    }
  }
}
